import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Box, Text, VStack, Spinner, Flex, useToast, Button, Icon, Menu, MenuButton, MenuList, MenuItem, SimpleGrid } from '@chakra-ui/react';
import { FiCheck, FiChevronDown, FiLock } from 'react-icons/fi';
import {
  ClientWithLocations,
  CampaignClientPair,
  type MarketplaceOpportunityListItemDto,
} from '@foodfluence/shared';
import CampaignComponent from './components/CampaignComponent';
import OpportunityCard from './components/OpportunityCard';
import { exploreApi } from '../../services/api/explore';
import { lcApi } from '../../services/api/lc';
import BrandsRow, { BrandItem } from './components/BrandsRow';
import FilterBar from './components/FilterBar';
import LocationFilter from './components/LocationFilter';
import { InfluencerDataContext } from '../../contexts/InfluencerDataContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { shouldShowVerificationOverlay } from '../../utils/authUtils';
import PhoneVerificationModal from '../../components/PhoneVerificationModal';
import { DisplayHeading, EmptyState, UnderlineTabs } from '../../components/ui';
import { ff, navClearance } from '../../theme';
import { exploreCacheKey, readExploreCache, saveExploreScroll, writeExploreCache } from './exploreCache';
import {
  SectionKey,
  SectionResult,
  SectionsData,
  ClientSection,
  CampaignSection,
  ClientSectionKey,
  ExploreSort,
  ExploreTab,
  EXPLORE_SORTS,
  EXPLORE_TABS,
  buildFeed,
  getFeedSections,
  sortFeed,
  isCampaignSection,
  createSectionResult,
  getInitialSectionsData,
  loadSavedFilters,
  initializeLocation
} from './utils';

/** Board sort glyph (up/down arrows); feather has no equivalent. */
const SortIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" />
  </svg>
);

/** Outline pill that opens a one-choice menu (Sort). */
function PillMenu<K extends string>({ label, icon, options, value, onChange }: {
  label: string;
  icon?: React.ReactElement;
  options: { key: K; label: string }[];
  value: K;
  onChange: (key: K) => void;
}) {
  const current = options.find(o => o.key === value)?.label;
  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        variant="outline"
        h="40px"
        minH="44px"
        px="14px"
        borderRadius="999px"
        border="1px solid"
        borderColor={ff.border}
        bg="white"
        _hover={{ bg: 'white' }}
        _active={{ bg: ff.grey }}
        fontSize="14px"
        fontWeight={600}
        lineHeight="normal"
        color={ff.ink}
        iconSpacing="8px"
        leftIcon={icon}
        rightIcon={<FiChevronDown size={14} strokeWidth={2.4} aria-hidden />}
        aria-label={`${label}: ${current}`}
      >
        {current}
      </MenuButton>
      <MenuList w="220px" minW="220px" p="6px" borderRadius="16px" border="1px solid" borderColor={ff.line} bg="white" boxShadow="0 12px 32px rgba(14,14,14,0.12)" zIndex={10}>
        {options.map(option => {
          const on = option.key === value;
          return (
            <MenuItem
              key={option.key}
              onClick={() => onChange(option.key)}
              h="44px"
              px="12px"
              borderRadius="10px"
              fontSize="15px"
              fontWeight={on ? 600 : 500}
              color={ff.ink}
              bg={on ? ff.limeSurface : 'transparent'}
              _hover={{ bg: on ? ff.limeSurface : ff.grey }}
              _focus={{ bg: on ? ff.limeSurface : ff.grey }}
              justifyContent="space-between"
              aria-checked={on}
              role="menuitemradio"
            >
              {option.label}
              {on && <Icon as={FiCheck} color={ff.accent} boxSize="16px" strokeWidth={2.6} aria-hidden />}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}

const RowHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as="h2" m={0} fontSize="15px" fontWeight={600} lineHeight="normal" mb="10px">
    {children}
  </Text>
);

/** True while a section the current tab shows is still on its first, unanswered load. */
const feedSectionsLoading = (sectionsData: SectionsData, sections: SectionKey[]) =>
  sections.some(key => sectionsData[key].pagination.loading && sectionsData[key].data.length === 0);

const ExplorePage: React.FC = () => {
  const toast = useToast();
  const [user] = useAuthState(auth);
  const { influencerData } = useContext(InfluencerDataContext);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  
  // Coming back to Explore (e.g. Home -> Explore) hydrates from the in-memory cache instead of refetching
  const [initial] = useState(() => {
    const savedFilters = loadSavedFilters();
    const initialFilters = {
      ...savedFilters,
      creatorTypes: influencerData?.creatorTypes || ['influencer']
    };
    return { filters: initialFilters, cached: readExploreCache(exploreCacheKey(auth.currentUser?.uid, initialFilters)) };
  });
  const cached = initial.cached;

  const [sectionsData, setSectionsData] = useState<SectionsData>(cached?.sectionsData ?? getInitialSectionsData());
  const [loading, setLoading] = useState(!cached);
  const [opportunities, setOpportunities] = useState<MarketplaceOpportunityListItemDto[]>(cached?.opportunities ?? []);

  const [filters, setFilters] = useState(initial.filters);
  const cacheKey = exploreCacheKey(user?.uid ?? auth.currentUser?.uid, filters);
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;
  const scrollYRef = useRef(cached?.scrollY ?? 0);
  
  // Update filters when influencer data changes
  useEffect(() => {
    if (influencerData?.creatorTypes) {
      setFilters(prev => ({
        ...prev,
        creatorTypes: influencerData.creatorTypes
      }));
    }
  }, [influencerData]);

  const [tab, setTab] = useState<ExploreTab>(cached?.tab ?? 'all');
  const [ugcOnly, setUgcOnly] = useState(cached?.ugcOnly ?? false);
  const [sort, setSort] = useState<ExploreSort>(cached?.sort ?? 'recommended');
  const [brandsExpanded, setBrandsExpanded] = useState(cached?.brandsExpanded ?? false);
  // UGC-only creators only get UGC sections (and no brands) from the backend
  const canSeeInfluencer = !filters.creatorTypes || filters.creatorTypes.includes('influencer');

  // Pagination sentinels: one at the feed end, one at the end of the brands row
  const feedEndRef = useRef<HTMLDivElement | null>(null);
  const brandsEndRef = useRef<HTMLDivElement | null>(null);

  const fetchSectionData = async (
    section: SectionKey,
    isInitial: boolean = false,
    filtersToUse: typeof filters
  ): Promise<SectionResult | null> => {
    try {
      if (isInitial) {
        setLoading(true);
      }
      
      // Update loading state
      setSectionsData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          pagination: { ...prev[section].pagination, loading: true }
        }
      }));

      const cursor = isInitial ? null : sectionsData[section].pagination.cursor;
      const response = await exploreApi.getExploreData(
        section, 
        cursor,
        filtersToUse.searchQuery || filtersToUse.location ? {
          searchQuery: filtersToUse.searchQuery,
          location: filtersToUse.location
        } : undefined
      );
      
      const result = createSectionResult(section, response.data, response.cursor || null, response.hasMore);
      
      // Update section data
      // Replace the section object instead of mutating it, so no other section (or the cached
      // snapshot) that shares a reference picks up this section's rows.
      setSectionsData(prev => {
        const data = isInitial ? result.data : [...prev[section].data, ...result.data];
        const pagination = { cursor: result.cursor, hasMore: result.hasMore, loading: false };
        return {
          ...prev,
          [section]: isCampaignSection(section)
            ? ({ data: data as CampaignClientPair[], pagination } as CampaignSection)
            : ({ data: data as ClientWithLocations[], pagination } as ClientSection),
        };
      });

      return result;
    } catch (err) {
      console.error(`Error fetching ${section}:`, err);
      toast({
        title: 'Error',
        description: `Failed to load ${section}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      
      // Reset loading state on error
      setSectionsData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          pagination: { ...prev[section].pagination, loading: false }
        }
      }));
      
      return null;
    }
  };

  const fetchAllSections = async (isInitial: boolean = false, filterOverride?: typeof filters) => {
    const filtersToUse = filterOverride || filters;
    setLoading(true);

    if (isInitial) {
      try {
        const response = await exploreApi.getAllSections({
          ...filtersToUse,
          creatorTypes: filtersToUse.creatorTypes
        });
        setSectionsData({
          featuredBrands: {
            data: response.featuredBrands || [],
            pagination: {
              cursor: response.featuredBrandsCursor || null,
              hasMore: response.featuredBrandsHasMore || false,
              loading: false
            }
          },
          allBrands: {
            data: response.allBrands || [],
            pagination: {
              cursor: response.allBrandsCursor || null,
              hasMore: response.allBrandsHasMore || false,
              loading: false
            }
          },
          activeCampaigns: {
            data: response.activeCampaigns || [],
            pagination: {
              cursor: response.activeCampaignsCursor || null,
              hasMore: response.activeCampaignsHasMore || false,
              loading: false
            }
          },
          activeUGCCampaigns: {
            data: response.activeUGCCampaigns || [],
            pagination: {
              cursor: response.activeUGCCampaignsCursor || null,
              hasMore: response.activeUGCCampaignsHasMore || false,
              loading: false
            }
          },
          inStoreCampaigns: {
            data: response.inStoreCampaigns || [],
            pagination: {
              cursor: response.inStoreCampaignsCursor || null,
              hasMore: response.inStoreCampaignsHasMore || false,
              loading: false
            }
          },
          inStoreUGCCampaigns: {
            data: response.inStoreUGCCampaigns || [],
            pagination: {
              cursor: response.inStoreUGCCampaignsCursor || null,
              hasMore: response.inStoreUGCCampaignsHasMore || false,
              loading: false
            }
          },
          retailCampaigns: {
            data: response.retailCampaigns || [],
            pagination: {
              cursor: response.retailCampaignsCursor || null,
              hasMore: response.retailCampaignsHasMore || false,
              loading: false
            }
          },
          retailUGCCampaigns: {
            data: response.retailUGCCampaigns || [],
            pagination: {
              cursor: response.retailUGCCampaignsCursor || null,
              hasMore: response.retailUGCCampaignsHasMore || false,
              loading: false
            }
          },
          shipToHomeCampaigns: {
            data: response.shipToHomeCampaigns || [],
            pagination: {
              cursor: response.shipToHomeCampaignsCursor || null,
              hasMore: response.shipToHomeCampaignsHasMore || false,
              loading: false
            }
          },
          shipToHomeUGCCampaigns: {
            data: response.shipToHomeUGCCampaigns || [],
            pagination: {
              cursor: response.shipToHomeUGCCampaignsCursor || null,
              hasMore: response.shipToHomeUGCCampaignsHasMore || false,
              loading: false
            }
          }
        });
        setLoading(false);
        return;
      } catch (err) {
        console.error('Error fetching all sections:', err);
        setLoading(false);
        return;
      }
    }

    // Determine which creator types to fetch
    const shouldFetchInfluencer = !filtersToUse.creatorTypes || filtersToUse.creatorTypes.includes('influencer');
    const shouldFetchUGC = true;


    // Prepare sections to fetch
    const sectionsToFetch = [];

    // Only fetch brands for influencer creators
    if (shouldFetchInfluencer) {
      sectionsToFetch.push(
        fetchSectionData('featuredBrands', isInitial, filtersToUse),
        fetchSectionData('allBrands', isInitial, filtersToUse),
        fetchSectionData('activeCampaigns', isInitial, filtersToUse),
        fetchSectionData('inStoreCampaigns', isInitial, filtersToUse),
        fetchSectionData('retailCampaigns', isInitial, filtersToUse),
        fetchSectionData('shipToHomeCampaigns', isInitial, filtersToUse)
      );
    }

    if (shouldFetchUGC) {
      sectionsToFetch.push(
        fetchSectionData('activeUGCCampaigns', isInitial, filtersToUse),
        fetchSectionData('inStoreUGCCampaigns', isInitial, filtersToUse),
        fetchSectionData('retailUGCCampaigns', isInitial, filtersToUse),
        fetchSectionData('shipToHomeUGCCampaigns', isInitial, filtersToUse)
      );
    }

    await Promise.all(sectionsToFetch);
    setLoading(false);
  };

  useEffect(() => {
    if (influencerData?.hasMarketplaceAccess !== true) return;
    lcApi.listOpportunities()
      .then(res => { if (res.success) setOpportunities(res.data); })
      .catch(() => { /* non-critical — omit silently */ });
  }, [influencerData?.hasMarketplaceAccess]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('explore_filters', JSON.stringify(filters));
  }, [filters]);

  // Snapshot the loaded state for the next visit
  useEffect(() => {
    if (loading) return;
    writeExploreCache({ key: cacheKey, sectionsData, opportunities, tab, ugcOnly, sort, brandsExpanded, scrollY: scrollYRef.current });
  }, [loading, cacheKey, sectionsData, opportunities, tab, ugcOnly, sort, brandsExpanded]);

  // Track scroll; restore it after a cache hydrate and remember it on leave
  useEffect(() => {
    if (cached) requestAnimationFrame(() => window.scrollTo(0, cached.scrollY));
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      saveExploreScroll(cacheKeyRef.current, scrollYRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize location and fetch data (skipped when hydrated from the cache)
  useEffect(() => {
    if (cached) return;
    const initializeAndFetch = async () => {
      // Try to initialize location
      const newFilters = await initializeLocation(filters);
      if (newFilters !== filters) {
        setFilters(newFilters);
        fetchAllSections(true, newFilters); // Fetch with new filters immediately
      } else {
        // Fetch with current filters if no location update needed
        fetchAllSections(true, filters);
      }
    };

    initializeAndFetch();
  }, []); // Empty dependency array since we want this to run only on mount

  const feedSections = useMemo(() => getFeedSections(tab, ugcOnly, canSeeInfluencer), [tab, ugcOnly, canSeeInfluencer]);
  const feed = useMemo(() => buildFeed(sectionsData, feedSections), [sectionsData, feedSections]);
  const feedItems = useMemo(() => sortFeed(feed.items, sort), [feed.items, sort]);

  // Featured brands first, then new brands (ringed) once the featured list is exhausted, de-duplicated
  const featured = sectionsData.featuredBrands as ClientSection;
  const newBrands = sectionsData.allBrands as ClientSection;
  const brands = useMemo<BrandItem[]>(() => {
    const seen = new Set<string>();
    const items: BrandItem[] = [];
    const add = (list: ClientWithLocations[], isNew: boolean) =>
      list.forEach(client => {
        if (seen.has(client.id)) return;
        seen.add(client.id);
        items.push({ client, isNew });
      });
    add(featured.data, false);
    if (!featured.pagination.hasMore) add(newBrands.data, true);
    return items;
  }, [featured, newBrands]);
  const nextBrandSection: ClientSectionKey | undefined = featured.pagination.hasMore
    ? 'featuredBrands'
    : newBrands.pagination.hasMore
      ? 'allBrands'
      : undefined;

  // Load the next page of whichever section a visible sentinel belongs to.
  // Re-created on every data change, so a sentinel still in view after a page lands fires again.
  useEffect(() => {
    // The initial all-sections load owns the first page of every section
    if (loading) return;
    const targets: [HTMLElement | null, SectionKey | undefined][] = [
      [feedEndRef.current, feed.nextSection],
      [brandsEndRef.current, canSeeInfluencer ? nextBrandSection : undefined],
    ];
    const observers = targets.flatMap(([el, section]) => {
      if (!el || !section) return [];
      const { hasMore, loading: sectionLoading } = sectionsData[section].pagination;
      if (!hasMore || sectionLoading) return [];
      const observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            observer.disconnect();
            fetchSectionData(section, false, filters);
          }
        },
        { root: null, rootMargin: '200px', threshold: 0.1 }
      );
      observer.observe(el);
      return [observer];
    });
    return () => observers.forEach(observer => observer.disconnect());
  }, [sectionsData, filters, feed.nextSection, nextBrandSection, brandsExpanded, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes
  const handleFiltersChange = (newFilters: typeof filters) => {
    // Save location preference if it changed
    if (newFilters.location !== filters.location) {
      localStorage.setItem('explore_location_preference', newFilters.location ? 'set' : 'cleared');
    }
    
    setFilters(newFilters);
    setSectionsData(getInitialSectionsData());
    fetchAllSections(true, newFilters);
  };

  const renderCard = ({ campaign, client }: CampaignClientPair) => (
    <CampaignComponent
      key={campaign.id}
      campaign={campaign}
      client={client}
      isExplorePage={true}
      locationFilter={filters.location}
    />
  );

  const showEmpty = !loading && feedItems.length === 0 && !feedSectionsLoading(sectionsData, feedSections);
  const countLabel = `${feedItems.length}${feed.nextSection ? '+' : ''} campaign${feedItems.length === 1 && !feed.nextSection ? '' : 's'}`;

  return (
    <Box minHeight="100vh" position="relative" bg="white" fontFamily="display" color={ff.ink}>
      {/* Full-page loading overlay */}
      {loading && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          align="center"
          justify="center"
          bg="rgba(255,255,255,0.8)"
          zIndex={9999}
        >
          <Spinner size="xl" thickness="4px" color={ff.ink} speed="0.65s" />
        </Flex>
      )}

      <Box maxW="1200px" mx="auto">
      <VStack px="20px" pt="24px" spacing="18px" align="stretch">
        <Flex align="flex-end" justify="space-between" gap="12px">
          <DisplayHeading>Explore</DisplayHeading>
          <LocationFilter
            location={filters.location}
            searchRadius={filters.searchRadius}
            onLocationChange={(loc, radius) => {
              handleFiltersChange({
                ...filters,
                location: loc,
                searchRadius: radius
              });
            }}
          />
        </Flex>

        <FilterBar filters={filters} onFiltersChange={handleFiltersChange} />

        {canSeeInfluencer && (
          <BrandsRow
            brands={brands}
            expanded={brandsExpanded}
            onToggle={() => setBrandsExpanded(open => !open)}
            endRef={el => { brandsEndRef.current = el; }}
            loading={!!nextBrandSection && sectionsData[nextBrandSection].pagination.loading}
          />
        )}

        <UnderlineTabs
          tabs={EXPLORE_TABS}
          active={tab}
          onChange={key => setTab(key as ExploreTab)}
        />
      </VStack>

      <VStack px="20px" pt="14px" pb="32px" spacing="28px" align="stretch">
        <Flex position="relative" zIndex={3} align="center" justify="space-between" mb="-14px">
          <Text fontSize="14px" lineHeight="normal" color={ff.text2}>{countLabel}</Text>
          <Flex gap="8px">
            {canSeeInfluencer && (
              <Button
                h="40px"
                minH="44px"
                px="14px"
                borderRadius="999px"
                border="1px solid"
                borderColor={ugcOnly ? ff.ink : ff.border}
                bg={ugcOnly ? ff.ink : 'white'}
                color={ugcOnly ? ff.lime : ff.ink}
                _hover={{ bg: ugcOnly ? ff.ink : 'white' }}
                _active={{ bg: ugcOnly ? ff.ink : ff.grey }}
                fontSize="14px"
                fontWeight={600}
                lineHeight="normal"
                aria-pressed={ugcOnly}
                onClick={() => setUgcOnly(on => !on)}
              >
                UGC
              </Button>
            )}
            <PillMenu label="Sort campaigns" icon={<SortIcon />} options={EXPLORE_SORTS} value={sort} onChange={setSort} />
          </Flex>
        </Flex>

        {tab === 'all' && influencerData?.hasMarketplaceAccess === true && opportunities.length > 0 && (
          <Box as="section" aria-label="Local Creator Orders">
            <RowHeading>Local Creator Orders</RowHeading>
            <Flex gap="14px" overflowX="auto" mx="-20px" px="20px" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {opportunities.map(opp => (
                <OpportunityCard key={opp.id} opportunity={opp} />
              ))}
            </Flex>
          </Box>
        )}

        {feedItems.length > 0 && (
          // Phones: the board's single column; wider screens: 2 then 3 columns
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} rowGap="28px" columnGap="24px">
            {feedItems.map(renderCard)}
          </SimpleGrid>
        )}

        {showEmpty && (
          <EmptyState
            title={filters.searchQuery ? 'No matches' : 'Nothing here yet'}
            body={
              filters.searchQuery
                ? 'No campaigns match your search. Try another brand name or tab.'
                : 'No campaigns of this kind near you right now. Try another tab or filter, or widen your area.'
            }
          />
        )}

        {/* Feed pagination sentinel */}
        <Flex ref={feedEndRef} justify="center" minH="24px">
          {feed.nextSection && sectionsData[feed.nextSection].pagination.loading && <Spinner size="sm" color={ff.ink} />}
        </Flex>
      </VStack>
      </Box>

      {/* Verification overlay - locks the page, leaves the floating nav usable */}
      {shouldShowVerificationOverlay(user, influencerData) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={navClearance}
          width="100vw"
          bg="rgba(255, 255, 255, 0.7)"
          css={{
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          zIndex={1300}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {!isPhoneModalOpen && (
            <VStack spacing="16px" align="center" p="32px" maxW="360px" textAlign="center">
              <Flex w="64px" h="64px" bg={ff.ink} color={ff.lime} borderRadius="full" align="center" justify="center">
                <Icon as={FiLock} boxSize="28px" aria-hidden />
              </Flex>
              <DisplayHeading as="h2" size="29px">Verify to explore</DisplayHeading>
              <Text fontSize="15px" color={ff.text2} lineHeight="1.5">
                Quick verification to unlock all available campaigns and brands
              </Text>
              <Button variant="lime" h="56px" w="100%" mt="8px" fontSize="16px" onClick={() => setIsPhoneModalOpen(true)}>
                Verify now
              </Button>
            </VStack>
          )}
        </Box>
      )}

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
      />
    </Box>
  );
};

export default ExplorePage;
